import { Button, Loading, Modal } from "@/components/atoms";
import { useRemoveAccessMutation } from "@/redux/apis/assistants/assistantsApi";
import { AccessLink } from "@/redux/apis/assistants/types";
import { addToast } from "@/utils";
import { useClientScope } from "../ClientScopeContext";

interface RemoveAssistantModalProps {
  link: AccessLink | null;
  open: boolean;
  onClose: () => void;
}

/** Confirm modal — revoke an assistant's access. Threads the managed client's accountId when operator-scoped. */
export const RemoveAssistantModal = ({ link, open, onClose }: RemoveAssistantModalProps) => {
  // Operator-managed client when set; undefined in self-service (client portal).
  const { accountId } = useClientScope();
  const isManaged = !!accountId;
  const [removeAccess, { isLoading }] = useRemoveAccessMutation();
  const firstName = link?.party.name?.split(" ")[0] || "This assistant";

  const handleRemove = async () => {
    if (!link) return;
    try {
      await removeAccess({ id: link.id, accountId }).unwrap();
      addToast("success", `Removed ${link.party.name}'s access`);
      onClose();
    } catch {
      addToast("danger", "Failed to remove access. Please try again.");
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      variant="web-small"
      title={`Remove ${link?.party.name ?? "assistant"}'s access?`}
      description={
        isManaged
          ? `${firstName} will no longer be able to manage this client's bookings or account. Trips they've already booked won't be affected. They'll be notified that their access was removed.`
          : `${firstName} will no longer be able to manage your bookings or account. Trips they've already booked for you won't be affected. They'll be notified that their access was removed.`
      }
      renderButtons={() => (
        <>
          <Button variant="secondary" size="lg" onClick={onClose}>
            Keep Access
          </Button>
          <Button
            variant="custom"
            size="lg"
            className="bg-danger text-white hover:bg-danger-dark"
            onClick={handleRemove}
            disabled={isLoading}
          >
            Remove Access
          </Button>
        </>
      )}
    >
      {isLoading && <Loading />}
    </Modal>
  );
};
